import IconArrowInCircle from '@images/svg/arrow-in-circle.svg';
import IconPhone from '@images/svg/phone.svg';
import throttle from 'lodash/throttle';
import React, { useState } from 'react';
import { PHONE_NUMBER, PHONE_NUMBER_DISPLAY } from '../../constants/constants';
import { useCtaProvider } from '../../contexts/franchise/cta-provider.tsx';
import { useMicrosite } from '../../contexts/franchise/microsite-context';
import MobileLocationDrawer from '../franchise/mobile-location-drawer';
import formatPhoneLink from '../utils/format-phone-link';
import Link from "../primitives/link";
import { colors } from '../primitives/colors/index.tsx';
import Skeleton from '../primitives/skeleton/index.tsx';
import {useLocator} from "../../contexts/franchise/locator-provider";

const StickyButton = ({ inverse, handleChangeLocation, localCtaData }) => {
    const {
        isMicrosite,
        franchise: micrositeFranchise,
        isLocalCityPage
    } = useMicrosite();

    const [isLocationDetailVisible, setIsLocationDetailVisible] = useState(false);
    const [isBottomContentVisible, setIsBottomContentVisible] = useState(true);
    const lastScrollTop = React.useRef(0);

    const { franchise: locatorFranchise, loading } = useLocator();
    const { hideNationalCallCenter, hideFranchiseLink, overridePhoneNumber, overridePhoneNumberDescriptor } = useCtaProvider();

    if (!!hideNationalCallCenter && !!hideFranchiseLink && !overridePhoneNumber) {
        return null;
    }
    const franchise = localCtaData?.franchise || micrositeFranchise || locatorFranchise?.data;

    const phone = franchise?.mainPhone;

    const bottomContentShouldNeverBeVisible = !!isLocalCityPage || !!isMicrosite || !!hideNationalCallCenter || !!hideFranchiseLink;
    React.useEffect(() => {
        document.documentElement.style.overflow = isLocationDetailVisible
            ? 'hidden'
            : '';
    }, [isLocationDetailVisible]);

    React.useEffect(() => {
        if (bottomContentShouldNeverBeVisible) {
            return;
        };

        const scrollHandler = throttle(() => {
            const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

            if (currentScrollTop === 0 || lastScrollTop.current === 0) {
                setIsBottomContentVisible(true);
            } else {
                setIsBottomContentVisible(currentScrollTop < lastScrollTop.current);
            }

            lastScrollTop.current = currentScrollTop;
        }, 200);

        window.addEventListener('scroll', scrollHandler);

        return () => {
            window.removeEventListener('scroll', scrollHandler);
        };
    }, []);

    return (
        <>
            <div css={styles.footerMobileStickyWrapper(inverse, isBottomContentVisible)}>
                <Link
                    css={styles.footerMobileBigButton}
                    onClick={() => {
                        if (!bottomContentShouldNeverBeVisible) {
                            setIsLocationDetailVisible(true);
                        }
                    }}
                    to={overridePhoneNumber ? `tel:${formatPhoneLink(overridePhoneNumber)}` : (isMicrosite || isLocalCityPage) ? `tel:${formatPhoneLink(phone)}` : hideFranchiseLink ? `tel:${formatPhoneLink(PHONE_NUMBER)}` : ''}
                >
                    {(isMicrosite || isLocalCityPage) && !overridePhoneNumber && franchise?.tempOutOfService && phone && phone.includes('SERVPRO') && <p>National Call Center</p>}
                    <div css={styles.footerMobileBigButtonPhoneWrap}>
                        <IconPhone css={styles.iconPhone} />
                        <p css={styles.footerMobileBigButtonPhone}>
                            {overridePhoneNumber ? `${overridePhoneNumberDescriptor || `Call: ${formatPhoneLink(overridePhoneNumber)}`}` : (isMicrosite || isLocalCityPage) ? `Call: ${formatPhoneLink(phone)}` : hideFranchiseLink ? 'National Call Center' : 'Call for Service'}
                        </p>
                    </div>
                    {!bottomContentShouldNeverBeVisible && (
                        <div css={styles.arrowIconWrapper}>
                            <IconArrowInCircle css={styles.iconArrow} />
                        </div>
                    )}
                </Link>
                {!isMicrosite && (!hideFranchiseLink || !hideNationalCallCenter) && (
                    <div css={styles.mobileButtonsWrap}>
                        {!hideFranchiseLink && !isLocalCityPage && (
                            <Link css={styles.footerMobileButton} to={`tel:+${formatPhoneLink(phone)}`}>
                                Your Local SERVPRO
                                {' '}
                                <br />
                                {(phone && !loading) ? (
                                    <span css={styles.footerMobileButtonPhone}>{formatPhoneLink(phone)}</span>
                                ) : (
                                    <Skeleton css={styles.skeleton} />
                                )}
                            </Link>
                        )}
                        {!hideNationalCallCenter && !hideFranchiseLink && (
                            <>
                                {!hideFranchiseLink && <div css={styles.divider} />}
                                <Link css={styles.footerMobileButton} to={`tel: +${formatPhoneLink(PHONE_NUMBER)}`}>
                                    National Call Center
                                    {' '}
                                    <br />
                                    <span css={styles.footerMobileButtonPhone}>{PHONE_NUMBER_DISPLAY}</span>
                                </Link>
                            </>
                        )}
                    </div>
                )}
            </div>
            <MobileLocationDrawer
                onClose={() => setIsLocationDetailVisible(false)}
                isVisible={isLocationDetailVisible}
                handleChangeLocation={handleChangeLocation}
                localCtaData={localCtaData}
            />
        </>
    );
};

const styles = {
    mobileButtonsWrap: (isBottomContentVisible) => ({
        fontDisplay: "block",
        paddingLeft: '10px',
        paddingRight: '10px',
        transform: "translateX(0) translateY(0) rotate(0) skewX(0) skewY(0) scaleX(0) scaleY(0)",
        display: "flex",
        transition: "transform 0.15s ease-in",
        transform: !isBottomContentVisible && "translate3d(0px, 41px, 0px) !important",
        "@media (min-width: 376px)": {
            transform: !isBottomContentVisible && "translate3d(0px, 53px, 0px) !important"
        },
        "@media (min-width: 1024px)": {
            display: "none"
        },
    }),
    footerMobileStickyWrapper: (inverse, isBottomContentVisible) => ({
        maxWidth: "100vw",
        fontSize: "16px",
        zIndex: 250,
        color: inverse ? colors.primaryOrange : colors.shadesWhite,
        backgroundColor: inverse ? colors.shadesWhite : colors.primaryOrange,
        position: "fixed",
        bottom: "0",
        width: "100%",
        textAlign: "center",
        transitionProperty: "color, background-color, border-color, text-decoration-color, fill, stroke",
        transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        transitionDuration: "300ms",
        transition: "transform 0.15s ease-in",
        transform: !isBottomContentVisible && "translate3d(0px, 41px, 0px) !important",
        "@media (min-width: 376px)": {
            transform: !isBottomContentVisible && "translate3d(0px, 53px, 0px) !important",
        },
        "@media (min-width: 1024px)": {
            display: "none",
        },
    }),
    footerMobileBigButtonPhoneWrap: {
        display: "flex",
        position: "relative",
        alignItems: "center"
    },
    footerMobileBigButtonPhone: {
        fontWeight: 600,
        letterSpacing: "0.025em",
        "@media (min-width: 375px)": {
            fontSize: "1.125rem",
            lineHeight: "1.75rem"
        }
    },
    footerMobileBigButton: {
        display: "flex",
        position: "relative",
        paddingTop: "1rem",
        paddingBottom: "1rem",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        "@media (min-width: 375px)": {
            paddingTop: "1.5rem",
            paddingBottom: "1.5rem"
        },
        "@media (min-width: 1024px)": {
            display: "none"
        }
    },
    divider: {
        marginLeft: "0.25rem",
        marginRight: "0.25rem",
        width: "1px",
        height: "30px",
        background: "currentColor",
        "@media (min-width: 375px)": {
            height: "40px",
        }
    },

    footerMobileButtonPhone: {
        fontSize: "0.875rem",
        lineHeight: "1.25rem",
        "@media (min-width: 375px)": {
            fontSize: "1.125rem",
            lineHeight: "1.75rem"
        }
    },
    footerMobileButton: {
        fontSize: "12px",
        flexGrow: 1,
        textAlign: "center",
        fontWeight: 600,
        lineHeight: 1,
        padding: "0 3px 7px 10px",
        whiteSpace: "nowrap",
        "@media (min-width: 375px)": {
            fontSize: "16px",
            padding: "0 5px 10px"
        }
    },
    iconPhone: {
        marginRight: "0.75rem",
        fill: "currentColor",
        transition: "fill 300ms cubic-bezier(0.4, 0, 0.2, 1)"
    },
    iconArrow: {
        width: "20px",
        fill: "currentColor",
        transition: "fill 300ms cubic-bezier(0.4, 0, 0.2, 1)"
    },
    arrowIconWrapper: {
        display: "flex",
        alignItems: "center",
        position: "absolute",
        top: "0",
        bottom: "0",
        right: "1rem"
    },
    skeleton: {
        maxWidth: '180px',
        margin: '6px auto 0',
        height: '16px'
    },
};

export default StickyButton;
